import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import i18n from "i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { LuSettings2, LuChevronRight } from "react-icons/lu";
import ColorPicker from "../../common/color-picker/ColorPicker";
import LiveNumberInput from "../../common/LiveNumberInput";
import FontFamilyButton from "./FontFamilyButton";
import "./_assessmentMenu.scss";

/* eslint-disable react/prop-types */

// Focused over-box "top pill" for non-assessment elements with a clear single
// purpose (OneClick = interactivity, image = change/fit). Reuses the assessment
// pill's look (.am-pill / .am-ic) and the same visible portal (#topPillRoot), and
// is anchored to the selected box's top-right corner via the left/top that
// EditorShortcuts.resize() already computes. Assessment boxes keep their richer
// AssessmentBoxMenu; this is the lightweight pill for everything purpose-driven.
//
// `more` (optional): rows for a gear-expanded "Daha fazla seçenek" popover,
// reusing AssessmentBoxMenu's .am-popover / .am-row / .am-toggle look. Each row
// is { key, label, control: "toggle", value, onChange(next) };
// optional `hint` becomes the row tooltip.
export default function TopActionPill({ left, top, actions, more }) {
  const items = (actions || []).filter(Boolean);
  const rows = (more || []).filter(Boolean);
  const [open, setOpen] = useState(false);
  // Placement, chosen from the pill's viewport position when the popover opens:
  //  - "right"     (default) → to the right of the pill; never covers the element.
  //  - "up-left"   → near the right edge but with room above: flips above-left.
  //  - "down-left" → near the right AND top edges (corner): opens down over the
  //                  element (the original behaviour) so it can't clip off-screen.
  const [placement, setPlacement] = useState("right");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open || !rootRef.current) return;
    const POPOVER_W = 300; // matches .am-popover width in _assessmentMenu.scss
    const GAP = 8;
    const estHeight = 60 + rows.length * 48; // rough popover height for edge checks
    const rect = rootRef.current.getBoundingClientRect();
    if (window.innerWidth - rect.right >= POPOVER_W + GAP) {
      setPlacement("right");
    } else if (rect.top >= estHeight + GAP) {
      setPlacement("up-left");
    } else {
      setPlacement("down-left");
    }
  }, [open, rows.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDocDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (!items.length && !rows.length) return null;

  const stop = (e) => e.stopPropagation();
  const pill = (
    <div
      ref={rootRef}
      className="assessmentBoxMenu am-pill"
      role="toolbar"
      aria-label="Element options"
      // `--pill-scale` (set on #topPillRoot by EditorCanvasSli) shrinks the pill
      // with the stage; the bottom-right origin keeps it hugging the box corner.
      style={{
        position: "absolute",
        left,
        top,
        transform: "translateX(-100%) scale(var(--pill-scale, 1))",
        transformOrigin: "100% 100%",
      }}
      onMouseDown={stop}
      onClick={stop}
    >
      {items.map((item) => {
        if (item.control === "font-picker") {
          return (
            <FontFamilyButton
              key={item.key}
              label={item.label}
              value={item.value}
              onChange={item.onChange}
              onPreview={item.onPreview}
              anchorBoxId={item.anchorBoxId}
              buttonClassName="am-ic"
            />
          );
        }
        // Inline stepper (e.g. font size) — same −/value/+ shape as the
        // popover's stepper row, sized to sit directly in the icon bar
        // instead of a click away behind the gear.
        if (item.control === "stepper") {
          const sval = item.value == null || item.value === "" ? (item.base ?? 0) : item.value;
          const min = item.min ?? -Infinity;
          const max = item.max ?? Infinity;
          const step = item.step ?? 1;
          return (
            <span
              key={item.key}
              className="am-stepper-inline"
              role="group"
              aria-label={item.label}
              title={item.label}
            >
              <button
                type="button"
                aria-label={`${item.label} -`}
                disabled={sval <= min}
                onClick={() => item.onChange(Math.max(min, sval - step))}
              >
                −
              </button>
              <b>{sval}</b>
              <button
                type="button"
                aria-label={`${item.label} +`}
                disabled={sval >= max}
                onClick={() => item.onChange(Math.min(max, sval + step))}
              >
                +
              </button>
            </span>
          );
        }
        return (
          <button
            key={item.key}
            type="button"
            className={
              "am-ic" + (item.danger ? " am-danger" : "") + (item.active ? " am-pressed" : "")
            }
            aria-pressed={item.active || undefined}
            aria-label={item.label}
            title={(item.disabled && item.disabledHint) || item.label}
            disabled={!!item.disabled}
            onClick={item.onClick}
          >
            <item.Icon className={item.iconClassName} />
          </button>
        );
      })}

      {rows.length > 0 && (
        <>
          {items.length > 0 && <span className="am-div" />}
          <button
            type="button"
            className={"am-ic am-accent" + (open ? " am-open" : "")}
            aria-label={i18n.t("AssessmentMenu.moreOptions")}
            title={i18n.t("AssessmentMenu.moreOptions")}
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <LuSettings2 />
          </button>
        </>
      )}

      {open && rows.length > 0 && (
        <div
          className="assessmentBoxMenu am-popover"
          role="group"
          aria-label={i18n.t("AssessmentMenu.moreOptions")}
          // Open to the RIGHT of the pill (not below) so it doesn't cover the
          // element (e.g. a video). Near the right edge, flip above-left; in the
          // top-right corner, fall back to opening downward over the element.
          style={
            placement === "up-left"
              ? {
                  position: "absolute",
                  bottom: "100%",
                  right: 0,
                  marginBottom: 8,
                  transformOrigin: "bottom right",
                }
              : placement === "down-left"
                ? {
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 8,
                    transformOrigin: "top right",
                  }
                : {
                    position: "absolute",
                    top: 0,
                    left: "100%",
                    marginLeft: 8,
                    transformOrigin: "top left",
                  }
          }
        >
          {rows.map((row) => {
            // Editable text row (e.g. the video URL) — label stacked over a
            // full-width input so a long URL stays visible/editable inline.
            if (row.control === "text") {
              return (
                <div className="am-row am-stack" key={row.key}>
                  <span className="am-lbl" id={`am-txt-${row.key}`}>
                    {row.label}
                  </span>
                  <input
                    type="text"
                    className="am-txt"
                    aria-labelledby={`am-txt-${row.key}`}
                    value={row.value == null ? "" : row.value}
                    placeholder={row.placeholder}
                    onChange={(e) => row.onChange(e.target.value)}
                  />
                </div>
              );
            }
            // Slider row (e.g. the image zoom/scale) — label + value readout
            // stacked over a full-width range input, matching the text row look.
            if (row.control === "range") {
              const rval = row.value == null ? (row.min ?? 0) : row.value;
              return (
                <div className="am-row am-stack" key={row.key}>
                  <span className="am-lbl" id={`am-rng-${row.key}`}>
                    {row.label}
                    <span className="am-rng-val">{rval}</span>
                  </span>
                  <input
                    type="range"
                    className="am-rng"
                    aria-labelledby={`am-rng-${row.key}`}
                    min={row.min ?? 0}
                    max={row.max ?? 100}
                    step={row.step ?? 1}
                    value={rval}
                    onChange={(e) => row.onChange(Number(e.target.value))}
                  />
                </div>
              );
            }
            // Stepper row (e.g. font size) — label + a −/value/+ control, no
            // free-typed pixel input. Matches AssessmentBoxMenu's stepper look.
            if (row.control === "stepper") {
              const sval = row.value == null || row.value === "" ? (row.base ?? 0) : row.value;
              const min = row.min ?? -Infinity;
              const max = row.max ?? Infinity;
              const step = row.step ?? 1;
              return (
                <div className="am-row" key={row.key}>
                  <span className="am-lbl">{row.label}</span>
                  <span className="am-stepper">
                    <button
                      type="button"
                      aria-label={`${row.label} -`}
                      disabled={sval <= min}
                      onClick={() => row.onChange(Math.max(min, sval - step))}
                    >
                      −
                    </button>
                    <b>{sval}</b>
                    <button
                      type="button"
                      aria-label={`${row.label} +`}
                      disabled={sval >= max}
                      onClick={() => row.onChange(Math.min(max, sval + step))}
                    >
                      +
                    </button>
                  </span>
                </div>
              );
            }
            // Dropdown row (e.g. font family) — label stacked over a full-width select.
            if (row.control === "select") {
              return (
                <div className="am-row am-stack" key={row.key}>
                  <span className="am-lbl" id={`am-sel-${row.key}`}>
                    {row.label}
                  </span>
                  <select
                    className="am-select"
                    aria-labelledby={`am-sel-${row.key}`}
                    value={row.value == null ? "" : row.value}
                    onChange={(e) => row.onChange(e.target.value)}
                  >
                    {row.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
            // Number pair (e.g. Width + Height) — two compact fields side by side,
            // each with its own label above and optional disabled/unit-suffix,
            // matching the right-panel "Yapı" width/height cell pair.
            if (row.control === "number-pair") {
              return (
                <div className="am-num-pair" key={row.key}>
                  {row.fields.map((f) => (
                    <div className="am-num-cell" key={f.key}>
                      <span className="am-num-cell-lbl" id={`am-num-${f.key}`}>
                        {f.label}
                      </span>
                      <span className="am-num-wrap">
                        <LiveNumberInput
                          className="am-num"
                          aria-labelledby={`am-num-${f.key}`}
                          min={f.min}
                          max={f.max}
                          disabled={!!f.disabled}
                          value={f.value}
                          onChange={f.onChange}
                        />
                        {f.suffix && <span className="am-num-suffix">{f.suffix}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }
            // Number row (e.g. font size) — label + a compact numeric input,
            // optionally disabled with a unit/"Auto" suffix.
            if (row.control === "number") {
              return (
                <div className="am-row" key={row.key}>
                  <span className="am-lbl" id={`am-num-${row.key}`}>
                    {row.label}
                  </span>
                  <span className="am-num-wrap">
                    <input
                      type="number"
                      className="am-num"
                      aria-labelledby={`am-num-${row.key}`}
                      min={row.min}
                      max={row.max}
                      disabled={!!row.disabled}
                      value={row.disabled ? "" : row.value == null ? "" : row.value}
                      onChange={(e) =>
                        row.onChange(e.target.value === "" ? "" : Number(e.target.value))
                      }
                    />
                    {row.suffix && <span className="am-num-suffix">{row.suffix}</span>}
                  </span>
                </div>
              );
            }
            // Color row — label stacked over the shared swatch+hex+alpha picker.
            if (row.control === "color") {
              return (
                <div className="am-row am-stack" key={row.key}>
                  <span className="am-lbl">{row.label}</span>
                  <ColorPicker value={row.value} onChange={(next) => row.onChange(next.color)} />
                </div>
              );
            }
            // Clickable action row (e.g. "change video" — opens a picker/modal).
            if (row.control === "action") {
              return (
                <div
                  className="am-row am-clickable"
                  role="button"
                  tabIndex={0}
                  key={row.key}
                  onClick={() => {
                    setOpen(false);
                    row.onClick();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpen(false);
                      row.onClick();
                    }
                  }}
                >
                  <span className="am-lbl">
                    {row.Icon ? <row.Icon /> : null}
                    {row.label}
                  </span>
                  <LuChevronRight />
                </div>
              );
            }
            const on = !!row.value;
            const toggleRow = (
              <div className="am-row" key={row.key}>
                <span className="am-lbl">{row.label}</span>
                <button
                  type="button"
                  className="am-toggle"
                  role="switch"
                  aria-checked={on}
                  aria-label={row.label}
                  onClick={() => row.onChange(!on)}
                />
              </div>
            );
            return row.hint ? (
              <OverlayTrigger
                key={row.key}
                placement="top"
                overlay={<Tooltip id={`pill-hint-${row.key}`}>{row.hint}</Tooltip>}
              >
                {toggleRow}
              </OverlayTrigger>
            ) : (
              toggleRow
            );
          })}
        </div>
      )}
    </div>
  );

  const portalRoot =
    document.getElementById("topPillRoot") || document.getElementById("editorBoxIconsRoot");

  return portalRoot ? ReactDOM.createPortal(pill, portalRoot) : pill;
}
